import React, { useState, useEffect } from "react";
import {
  Home,
  Calendar,
  Clock,
  UserPlus,
  AlertCircle,
  Search,
  ChevronDown,
  ChevronUp,
  Ban,
  Check,
  X,
} from "lucide-react";
import VisitForm from "./VisitForm";
import VisitQRModal from "./VisitQRModal";
import Header from "./Header";
import useAuthStore from "../store/useAuthStore";
import api from "../api/axios";
import { hoySV, diaISO, mostrarDia } from "../utils/fecha";
import { visitas } from "../api/modulos.service";
import { toast } from "react-toastify";
import { coincide } from "../utils/texto";
import { Card } from "./shared/Card";
import { Button } from "./shared/Button";
import { StatusBadge } from "./shared/Badge";
import { Table } from "./shared/Table";
import socketManager from "../api/socket";
import Navegador from "./modulos/Navegador";

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "var(--bgSecondary)",
  },
  content: {
    maxWidth: "1200px",
    margin: "0 auto",
    padding: "2rem 1rem",
  },
  header: {
    marginBottom: "2rem",
  },
  title: {
    fontSize: "1.875rem",
    fontWeight: "600",
    color: "var(--text)",
    marginBottom: "0.5rem",
  },
  subtitle: {
    fontSize: "1rem",
    color: "var(--textSecondary)",
  },
  welcomeCard: {
    background:
      "linear-gradient(135deg, var(--primary) 0%, var(--primaryDark) 100%)",
    color: "white",
    padding: "2rem",
    marginBottom: "2rem",
  },
  welcomeContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "1rem",
  },
  welcomeText: {
    flex: 1,
  },
  welcomeTitle: {
    fontSize: "1.5rem",
    fontWeight: "600",
    marginBottom: "0.5rem",
  },
  welcomeInfo: {
    display: "flex",
    alignItems: "center",
    gap: "1rem",
    opacity: 0.9,
    fontSize: "0.875rem",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "1rem",
    marginBottom: "2rem",
  },
  statCard: {
    padding: "1.5rem",
    display: "flex",
    alignItems: "center",
    gap: "1rem",
  },
  statIcon: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  statContent: {
    flex: 1,
  },
  statValue: {
    fontSize: "1.5rem",
    fontWeight: "600",
    color: "var(--text)",
  },
  statLabel: {
    fontSize: "0.875rem",
    color: "var(--textSecondary)",
  },
  addVisitSection: {
    marginBottom: "2rem",
  },
  formCollapse: {
    marginTop: "1rem",
    overflow: "hidden",
    transition: "all 0.3s ease-in-out",
  },
  searchContainer: {
    position: "relative",
    marginBottom: "var(--space-4)",
  },
  searchIcon: {
    position: "absolute",
    left: "var(--space-3)",
    top: "50%",
    transform: "translateY(-50%)",
    color: "var(--text-tertiary)",
  },
  searchInput: {
    width: "100%",
    padding: "var(--space-3) var(--space-4)",
    paddingLeft: "2.5rem",
    borderRadius: "var(--radius-lg)",
    border: "2px solid var(--border)",
    backgroundColor: "var(--bg-tertiary)",
    fontSize: "var(--text-base)",
    color: "var(--text)",
    transition: "all 0.2s ease",
    outline: "none",
    minHeight: "44px", // Touch-friendly size
  },
  searchInputFocus: {
    borderColor: "var(--gold)",
    backgroundColor: "var(--bg-secondary)",
    boxShadow: "0 0 0 4px var(--gold-subtle)",
  },
  recentVisitsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem",
  },
  sectionTitle: {
    fontSize: "1.25rem",
    fontWeight: "600",
    color: "var(--text)",
  },
  emptyState: {
    textAlign: "center",
    padding: "3rem",
    color: "var(--textSecondary)",
  },
  emptyIcon: {
    width: "48px",
    height: "48px",
    margin: "0 auto 1rem",
    color: "var(--textTertiary)",
  },
};

export default function ResidentDashboard() {
  const { user } = useAuthStore();
  const [refreshList, setRefreshList] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedVisitId, setHighlightedVisitId] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedVisitForQR, setSelectedVisitForQR] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    today: 0,
    thisWeek: 0,
  });
  const formRef = React.useRef(null);

  useEffect(() => {
    fetchVisits();
  }, [refreshList]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!user?.houseNumber || !user?.polygon) return;

    const socket = socketManager.get();
    socket.emit("watchHouse", {
      polygon: user.polygon,
      houseNumber: user.houseNumber,
    });

    const handleVisitUpdated = (updatedVisit) => {
      setVisits((prev) =>
        prev.map((v) => (v._id === updatedVisit._id ? updatedVisit : v))
      );
    };

    const handleVisitCreated = (newVisit) => {
      if (
        newVisit.polygon === user.polygon &&
        newVisit.houseNumber === user.houseNumber
      ) {
        setVisits((prev) => {
          // Check if visit already exists to avoid duplicates
          const exists = prev.find((v) => v._id === newVisit._id);
          if (exists) {
            return prev;
          }
          return [newVisit, ...prev];
        });
        
        // Only highlight if we don't already have a highlighted visit
        // and this visit isn't the one we just created locally
        if (!highlightedVisitId && newVisit._id !== highlightedVisitId) {
          setHighlightedVisitId(newVisit._id);
          setTimeout(() => {
            setHighlightedVisitId(null);
          }, 5000); // Remove highlight after 5 seconds
        }
      }
    };

    socket.on("visitUpdated", handleVisitUpdated);
    socket.on("visitCreated", handleVisitCreated);

    return () => {
      socket.off("visitUpdated", handleVisitUpdated);
      socket.off("visitCreated", handleVisitCreated);
    };
  }, [user, visits]);

  const fetchVisits = async (shouldHighlightNewest = false) => {
    // Store current highlight before loading
    const currentHighlight = highlightedVisitId;
    
    setLoading(true);
    try {
      const res = await api.get("/visits/mine");
      const visitList = res.data.visits || res.data || [];
      setVisits(visitList);
      
      // If we should highlight the newest visit (after form submission)
      if (shouldHighlightNewest && visitList.length > 0) {
        // Sort by date to find the newest
        const sortedVisits = [...visitList].sort((a, b) => 
          new Date(b.date) - new Date(a.date)
        );
        const newestVisit = sortedVisits[0];
        setHighlightedVisitId(newestVisit._id);
        setTimeout(() => {
          setHighlightedVisitId(null);
        }, 5000);
      } else if (currentHighlight) {
        // Preserve existing highlight if it still exists in the new data
        const stillExists = visitList.find(v => v._id === currentHighlight);
        if (stillExists) {
          setHighlightedVisitId(currentHighlight);
        }
      }

      // Calculate stats
      const now = new Date();
      const today = hoySV();   // en El Salvador, no en UTC
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const newStats = visitList.reduce(
        (acc, visit) => {
          acc.total++;
          if (visit.status === "Pending") acc.pending++;

          const visitDate = new Date(visit.date);
          /* Se comparan DÍAS, no instantes. `esHoySV` leía la fecha en la zona
             de El Salvador, pero el API la manda como medianoche UTC: en UTC-6
             eso es ayer a las 6 de la tarde, así que el contador contaba el día
             anterior. `diaISO` la lee en UTC —la zona en que se escribió— y
             `hoySV()` da hoy en El Salvador; ambas en `YYYY-MM-DD`. */
          if (diaISO(visit.date) === today) acc.today++;
          if (visitDate >= weekAgo) acc.thisWeek++;

          return acc;
        },
        { total: 0, pending: 0, today: 0, thisWeek: 0 }
      );

      setStats(newStats);
    } catch (err) {
      console.error("Error al obtener visitas", err);
      setVisits([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredVisits = visits
    .filter(
      (visit) =>
        coincide(visit.visitorName, searchTerm) ||
        (visit.note && coincide(visit.note, searchTerm))
    )
    .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date, newest first

  /**
   * REVOCAR UN PASE.
   *
   * El botón existía en `VisitList.jsx`, pero este panel importaba ese
   * componente y **nunca lo dibujaba**: el residente no tenía por dónde
   * revocar un pase que ya no quería que sirviera. Es lo primero que uno
   * busca cuando una visita se cancela.
   */
  const revocarPase = async (v) => {
    if (!window.confirm(`¿Revocar el pase de ${v.visitorName}? El código dejará de servir.`)) return;
    const motivo = window.prompt("Motivo (opcional):") || "Revocado por el residente";
    try {
      await visitas.revocar(v._id, { reason: motivo });
      toast.success("Pase revocado");
      fetchVisits();
    } catch (err) {
      toast.error(err?.response?.data?.message || "No se pudo revocar el pase");
    }
  };

  /**
   * AUTORIZAR O DENEGAR a alguien que llegó sin aviso.
   *
   * El guardia pide la autorización desde la caseta y queda en
   * `AwaitingApproval`. `visitas.decidir` existía en el cliente y **ningún
   * componente la llamaba**: el guardia podía preguntar y nadie podía
   * contestar. Con el interruptor `live_approval_enabled` encendido de
   * fábrica, era una función anunciada que no se podía usar.
   */
  const decidir = async (v, decision) => {
    const esAutorizar = decision === "autorizar";
    if (!window.confirm(
      esAutorizar
        ? `¿Autorizar la entrada de ${v.visitorName}?`
        : `¿Denegar la entrada de ${v.visitorName}?`
    )) return;
    const cuerpo = { decision };
    if (!esAutorizar) cuerpo.reason = window.prompt("Motivo (opcional):") || "No autorizado";
    try {
      await visitas.decidir(v._id, cuerpo);
      toast.success(esAutorizar ? "Acceso autorizado" : "Acceso denegado");
      fetchVisits();
    } catch (err) {
      toast.error(err?.response?.data?.message || "No se pudo registrar la decisión");
    }
  };

  const columns = [
    {
      key: "visitorName",
      label: "Visitante",
      render: (value) => <div style={{ fontWeight: "500" }}>{value}</div>,
    },
    {
      key: "date",
      label: "Fecha programada",
      render: (value) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Calendar size={14} style={{ color: "var(--textTertiary)" }} />
          {/* `mostrarDia`: la fecha llega como medianoche UTC y leerla en hora
              de El Salvador mostraba el día anterior en la columna. */}
          {mostrarDia(value, { dateStyle: "medium" })}
        </div>
      ),
    },
    {
      key: "note",
      label: "Motivo",
      render: (value) =>
        value || <span style={{ color: "var(--textTertiary)" }}>-</span>,
    },
    {
      key: "status",
      label: "Estado",
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: "entryTime",
      label: "Entrada",
      render: (value) =>
        value ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              fontSize: "0.75rem",
              color: "var(--success)",
            }}
          >
            <Clock size={12} />
            {new Date(value).toLocaleTimeString("es-SV", {
              timeZone: "America/El_Salvador",
              timeStyle: "short",
            })}
          </div>
        ) : (
          "-"
        ),
    },
    {
      key: "exitTime",
      label: "Salida",
      render: (value) =>
        value ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.25rem",
              fontSize: "0.75rem",
              color: "var(--info)",
            }}
          >
            <Clock size={12} />
            {new Date(value).toLocaleTimeString("es-SV", {
              timeZone: "America/El_Salvador",
              timeStyle: "short",
            })}
          </div>
        ) : (
          "-"
        ),
    },
    {
      key: "_id",
      label: "Acciones",
      render: (value, row) => {
        /* Qué se puede hacer depende del estado:
           · esperando autorización → el residente decide, que es el único que
             puede: el guardia pregunta, no responde por él.
           · pendiente → todavía se puede revocar.
           · ya entró, salió, revocada, denegada o vencida → nada que hacer. */
        if (row.status === "AwaitingApproval") {
          return (
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <Button size="sm" onClick={() => decidir(row, "autorizar")} leftIcon={<Check size={14} />}>
                Autorizar
              </Button>
              <Button size="sm" variant="danger" onClick={() => decidir(row, "denegar")} leftIcon={<X size={14} />}>
                Denegar
              </Button>
            </div>
          );
        }
        if (row.status === "Pending") {
          return (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => revocarPase(row)}
              leftIcon={<Ban size={14} />}
              aria-label={`Revocar el pase de ${row.visitorName}`}
            >
              Revocar
            </Button>
          );
        }
        return <span style={{ color: "var(--textTertiary)" }}>—</span>;
      },
    },
  ];

  return (
    <>
      <Header />
      <div style={styles.container}>
        <div style={styles.content}>
          <Card style={styles.welcomeCard}>
            <div style={styles.welcomeContent}>
              <div style={styles.welcomeText}>
                <h1 style={styles.welcomeTitle}>¡Bienvenido, {user?.name}!</h1>
                <div style={styles.welcomeInfo}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Home size={16} />
                    <span>
                      Casa {user?.houseNumber} - Polígono {user?.polygon}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <Calendar size={16} />
                    <span>
                      {new Date().toLocaleDateString("es-SV", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                onClick={() => {
                  setShowAddForm(true);
                  // Wait for the form to render, then scroll to it
                  setTimeout(() => {
                    if (formRef.current) {
                      const yOffset = -100; // Negative value to scroll a bit before the element
                      const y = formRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                      
                      window.scrollTo({
                        top: y,
                        behavior: 'smooth'
                      });
                    }
                  }, 100);
                }}
                leftIcon={<UserPlus size={18} />}
              >
                Nueva Visita
              </Button>
            </div>
          </Card>

          <div style={styles.stats}>
            <Card style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  backgroundColor: "var(--primaryBg)",
                  color: "var(--primary)",
                }}
              >
                <UserPlus size={24} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.total}</div>
                <div style={styles.statLabel}>Total de Visitas</div>
              </div>
            </Card>

            <Card style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  backgroundColor: "var(--warningBg)",
                  color: "var(--warning)",
                }}
              >
                <Clock size={24} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.pending}</div>
                <div style={styles.statLabel}>Pendientes</div>
              </div>
            </Card>

            <Card style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  backgroundColor: "var(--successBg)",
                  color: "var(--success)",
                }}
              >
                <Calendar size={24} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.today}</div>
                <div style={styles.statLabel}>Hoy</div>
              </div>
            </Card>

            <Card style={styles.statCard}>
              <div
                style={{
                  ...styles.statIcon,
                  backgroundColor: "var(--infoBg)",
                  color: "var(--info)",
                }}
              >
                <Calendar size={24} />
              </div>
              <div style={styles.statContent}>
                <div style={styles.statValue}>{stats.thisWeek}</div>
                <div style={styles.statLabel}>Esta Semana</div>
              </div>
            </Card>
          </div>

          {showAddForm && (
            <div ref={formRef} style={styles.addVisitSection}>
              <Card title="Registrar Nueva Visita" overflowVisible>
                <VisitForm
                  onVisitCreated={(newVisit) => {
                    setShowAddForm(false);
                    // If the form returns the new visit data, add it directly
                    if (newVisit && newVisit._id) {
                      setVisits(prev => {
                        // Check if visit already exists to avoid duplicates
                        const exists = prev.find((v) => v._id === newVisit._id);
                        if (exists) {
                          return prev;
                        }
                        return [newVisit, ...prev];
                      });
                      setHighlightedVisitId(newVisit._id);
                      setTimeout(() => {
                        setHighlightedVisitId(null);
                      }, 5000);
                      // Update stats
                      setStats(prev => ({
                        ...prev,
                        total: prev.total + 1,
                        pending: prev.pending + 1,
                        // Mismo cálculo de día que el contador de arriba, o el
                        // número cambiaría al crear una visita y volvería a su
                        // sitio al recargar.
                        today: diaISO(newVisit.date) === hoySV() ? prev.today + 1 : prev.today
                      }));
                      
                      // Siempre mostrar el modal si se generó QR
                      if (newVisit.qrCode && newVisit.qrToken) {
                        setSelectedVisitForQR(newVisit);
                        setShowQRModal(true);
                      }
                    } else {
                      // Fallback to fetching if no visit data returned
                      fetchVisits(true);
                    }
                  }}
                />
              </Card>
            </div>
          )}

          <Card>
            <div style={styles.recentVisitsHeader}>
              <h2 style={styles.sectionTitle}>Mis Visitas</h2>
              <Button
                variant="ghost"
                onClick={() => setRefreshList((prev) => !prev)}
                leftIcon={<Clock size={16} />}
              >
                Actualizar
              </Button>
            </div>

            <div style={styles.searchContainer}>
              <Search size={18} style={styles.searchIcon} />
              <input
                type="text"
                placeholder="Buscar por nombre o motivo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                style={{
                  ...styles.searchInput,
                  ...(searchFocused ? styles.searchInputFocus : {}),
                  ...(isMobile ? { fontSize: '16px' } : {}),
                }}
              />
            </div>

            {loading ? (
              <div style={styles.emptyState}>
                <div
                  className="animate-spin"
                  style={{
                    width: "2rem",
                    height: "2rem",
                    border: "2px solid var(--border)",
                    borderTopColor: "var(--primary)",
                    borderRadius: "50%",
                    margin: "0 auto",
                  }}
                ></div>
                <p style={{ marginTop: "1rem" }}>Cargando visitas...</p>
              </div>
            ) : filteredVisits.length === 0 ? (
              <div style={styles.emptyState}>
                <AlertCircle style={styles.emptyIcon} />
                <h3 style={{ marginBottom: "0.5rem" }}>No hay visitas</h3>
                <p>
                  {searchTerm
                    ? "No se encontraron visitas con ese criterio"
                    : "No has registrado ninguna visita aún"}
                </p>
                {!searchTerm && (
                  <Button
                    onClick={() => {
                      setShowAddForm(true);
                      // Wait for the form to render, then scroll to it
                      setTimeout(() => {
                        if (formRef.current) {
                          const yOffset = -100; // Negative value to scroll a bit before the element
                          const y = formRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
                          
                          window.scrollTo({
                            top: y,
                            behavior: 'smooth'
                          });
                        }
                      }, 100);
                    }}
                    leftIcon={<UserPlus size={16} />}
                    style={{ marginTop: "1rem" }}
                  >
                    Registrar Primera Visita
                  </Button>
                )}
              </div>
            ) : (
              <div className="responsive-table">
                <Table 
                  columns={columns} 
                  data={filteredVisits} 
                  hoverable
                  rowStyle={(row) => ({
                    backgroundColor: row._id === highlightedVisitId ? 'var(--gold-subtle)' : 'transparent',
                    animation: row._id === highlightedVisitId ? 'pulse-highlight 2s ease-in-out' : 'none',
                    transition: 'background-color 0.5s ease'
                  })}
                />
              </div>
            )}
          </Card>
        </div>
      </div>
      
      {/* Los módulos que se añadieron con el roadmap. Van debajo de lo
          que ya existía: quien usa esto a diario no debería tener que
          buscar de nuevo lo que hace todos los días. */}
      <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 var(--container-padding) var(--space-10)" }}>
        <Navegador rol="resident" />
      </div>

      {/* Modal para mostrar el QR */}
      {showQRModal && selectedVisitForQR && (
        <VisitQRModal
          visit={selectedVisitForQR}
          onClose={() => {
            setShowQRModal(false);
            setSelectedVisitForQR(null);
          }}
        />
      )}
    </>
  );
}
